import type maplibregl from "maplibre-gl";

type LayerKey = string;

type LayersConfig = Record<
  LayerKey,
  {
    defaultFilter?: maplibregl.FilterSpecification;
    defaultHidden?: boolean;
    defaultHiddenTypes?: string[];
    visibilityTypeKey?: string;
  }
>;

type SavedLayerFilters = Record<LayerKey, maplibregl.FilterSpecification>;
type SavedLayerVisibility = Record<
  LayerKey,
  {
    hidden?: boolean;
    hiddenTypes?: string[];
  }
>;

interface LayerState {
  filter?: maplibregl.FilterSpecification;
  hidden: boolean;
  hiddenTypes: string[];
}

function resolveInitialHiddenTypes(
  visibilityTypeKey: string | undefined,
  visibilityStateFromStorage: SavedLayerVisibility[string] | undefined,
  defaultHiddenTypes: string[],
): string[] {
  if (visibilityStateFromStorage === undefined) {
    return defaultHiddenTypes;
  }

  if (visibilityStateFromStorage.hiddenTypes !== undefined) {
    return visibilityStateFromStorage.hiddenTypes;
  }

  // Migrate the former single New Housing pins toggle to per-status visibility.
  if (visibilityTypeKey === "reviewStatus") {
    return visibilityStateFromStorage.hidden === false
      ? []
      : defaultHiddenTypes;
  }

  return [];
}

export function buildInitialLayers(
  config: LayersConfig,
  _saved: SavedLayerFilters | null,
  savedVisibility: SavedLayerVisibility | null,
): Record<LayerKey, LayerState> {
  return Object.keys(config).reduce<Record<LayerKey, LayerState>>(
    (out, key) => {
      const cfg = config[key];
      const visibilityStateFromStorage = savedVisibility?.[key];
      const storedHidden = visibilityStateFromStorage?.hidden;
      const defaultHiddenTypes = cfg.defaultHiddenTypes ?? [];
      // Type-driven layers ignore persisted parent `hidden` (UI only toggles hiddenTypes).
      // Clears stale `hidden: true` left by older hideAllLayers behavior.
      const hidden =
        cfg.visibilityTypeKey != null
          ? false
          : (storedHidden ?? cfg.defaultHidden ?? false);
      const hiddenTypes = resolveInitialHiddenTypes(
        cfg.visibilityTypeKey,
        visibilityStateFromStorage,
        defaultHiddenTypes,
      );

      out[key] = {
        filter: undefined,
        hidden,
        hiddenTypes,
      };
      return out;
    },
    {},
  );
}
